/** Ad click IDs only exist in the landing URL (Microsoft Ads `msclkid`, Google Ads
 * `gclid`/`gbraid`/`wbraid`; the ad tracking template may prefix them `utm_`).
 * They are captured by an inline <head> script before
 * hydration, so a quick navigation cannot lose them, and kept per network (last
 * click wins) for 90 days so every lead form can send them.
 *
 * The same script republishes them on every page load in three places, which is
 * why no internal link ever has to carry the ID in its URL:
 *   - `localStorage` — read by `getAdClickIds()` for lead payloads;
 *   - a first-party cookie per ID (`oh_msclkid`, …) on `.office-hub.com`, read
 *     natively by GTM's 1st-Party Cookie variable and sent to the server with
 *     every request, expiring exactly 90 days after the click;
 *   - `dataLayer`, pushed before GTM loads so every tag on every page resolves
 *     the ID plus an `<id>_present` flag.
 *
 * The same script also mints the anonymous `oh_vid` visitor ID (functional
 * consent, 400 days) and publishes it as `visitor_id` — no separate event, so
 * it is simply available to every tag and to every lead payload. */
export const AD_CLICK_ID_PARAMS = [
  "msclkid",
  "gclid",
  "gbraid",
  "wbraid",
] as const;
export type AdClickIds = Record<(typeof AD_CLICK_ID_PARAMS)[number], string>;

const STORAGE_KEY = "adClickIds";
/** JSON string read by lead forms via `getItem("landingUrl")` (~/utils/storage). */
const LANDING_URL_KEY = "landingUrl";
/** Page-memory holding area while advertising consent is not yet granted. */
const PENDING_GLOBAL = "__ohAdClickIds";
/** Last published state, for inspection from the console. Production replaces
 * `console.log` after this script has run, so a late log (the consent path)
 * is invisible there while `window.__ohAdClick` always is. */
const DEBUG_GLOBAL = "__ohAdClick";
/** One cookie per ID, e.g. `oh_msclkid` — a plain value, so GTM can read it
 * with a 1st-Party Cookie variable without any custom JavaScript. */
const COOKIE_PREFIX = "oh_";
/** Anonymous first-party visitor ID. Gated on **functional** consent, not
 * advertising: it stitches one person's own visits together so a lead can be
 * read back as a journey, it does not target ads. */
const VISITOR_ID_COOKIE = "oh_vid";
/** 400 days — Chrome caps cookie lifetime there, so asking for more is pointless. */
const VISITOR_ID_MAX_AGE_S = 400 * 24 * 60 * 60;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

type StoredIds = Partial<Record<keyof AdClickIds, { v: string; t: number }>>;
type PendingCapture = { ids: StoredIds; landingUrl: string };

/** Inline <head> script. Storage is allowed when the stored cookie consent grants
 * advertising, or — with no stored decision — when the region needs no consent
 * (same rule as the Consent Mode defaults). Otherwise the IDs stay in page memory
 * until `persistPendingAdClickIds` runs on consent. */
export function adClickCaptureScript(consentRequired: boolean): string {
  return `(function(){try{
var keys=${JSON.stringify(AD_CLICK_ID_PARAMS)},q=new URLSearchParams(location.search),now=Date.now(),found={},any=false;
keys.forEach(function(k){var v=q.get(k)||q.get('utm_'+k);if(v){found[k]={v:v,t:now};any=true;}});
var dflt=${JSON.stringify(!consentRequired)},adOk=dflt,fnOk=dflt,consent=localStorage.getItem('cookieConsent');
if(consent){var prefs=JSON.parse(consent).preferences||{};adOk=!!prefs.advertising;fnOk=!!prefs.functional;}
var dom=/(^|\.)office-hub\.com$/.test(location.hostname)?';domain=.office-hub.com':'',sec=location.protocol==='https:'?';Secure':'',dl={},wrote=[];
var vid='';
if(fnOk){
var cks=document.cookie.split(';');for(var ci=0;ci<cks.length;ci++){var kv=cks[ci].split('=');if(kv[0].trim()==='${VISITOR_ID_COOKIE}'){vid=kv.slice(1).join('=');break;}}
if(!vid){vid=(window.crypto&&crypto.randomUUID)?crypto.randomUUID():'v'+now.toString(36)+Math.random().toString(36).slice(2,10);wrote.push('${VISITOR_ID_COOKIE} (new)');}
document.cookie='${VISITOR_ID_COOKIE}='+vid+';max-age=${VISITOR_ID_MAX_AGE_S};path=/'+dom+sec+';SameSite=Lax';
}else{document.cookie='${VISITOR_ID_COOKIE}=;max-age=0;path=/'+dom+sec+';SameSite=Lax';}
dl.visitor_id=vid;
keys.forEach(function(k){dl[k]='';dl[k+'_present']=false;});
if(!adOk){
if(any){window.${PENDING_GLOBAL}={ids:found,landingUrl:location.href};}
console.log('[ad-click] advertising consent not granted - held in page memory:',Object.keys(found));
}else{
var s={};try{s=JSON.parse(localStorage.getItem('${STORAGE_KEY}')||'{}');}catch(e){}
if(any){for(var k in found){s[k]=found[k];}localStorage.setItem('${STORAGE_KEY}',JSON.stringify(s));}
if(any||!localStorage.getItem('${LANDING_URL_KEY}')){localStorage.setItem('${LANDING_URL_KEY}',JSON.stringify(location.href));}
keys.forEach(function(k){
var e=s[k],age=e?now-e.t:0,v=e&&age<${MAX_AGE_MS}?e.v:'';
dl[k]=v;dl[k+'_present']=!!v;
if(v){document.cookie='${COOKIE_PREFIX}'+k+'='+encodeURIComponent(v)+';max-age='+Math.floor((${MAX_AGE_MS}-age)/1000)+';path=/'+dom+sec+';SameSite=Lax';wrote.push('${COOKIE_PREFIX}'+k+' ('+Math.round((${MAX_AGE_MS}-age)/86400000)+'d left)');}
});
}
if(adOk||fnOk){
window.dataLayer=window.dataLayer||[];window.dataLayer.push(dl);
window.${DEBUG_GLOBAL}={dataLayer:dl,cookies:wrote,fromUrl:Object.keys(found),at:new Date().toISOString()};
console.log('[ad-click] published',{fromUrl:Object.keys(found),cookies:wrote,dataLayer:dl});
}
}catch(e){console.log('[ad-click] capture failed',e);}})();`;
}

function readStoredIds(): StoredIds {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") as StoredIds;
  } catch {
    return {};
  }
}

function readPending(): PendingCapture | undefined {
  return (window as unknown as Record<string, PendingCapture | undefined>)[
    PENDING_GLOBAL
  ];
}

/** Original page for this visit/campaign. Unlike Pardot Last URL, this value is
 * intentionally retained when the visitor moves to another Office Hub page. */
export function getLandingPageUrl(): string {
  if (typeof window === "undefined") return "";

  const pendingLandingUrl = readPending()?.landingUrl;
  if (pendingLandingUrl) return pendingLandingUrl;

  try {
    const stored = JSON.parse(
      localStorage.getItem(LANDING_URL_KEY) || '""',
    ) as string;
    if (stored) return stored;
  } catch {
    // Ignore malformed legacy storage and use the current page below.
  }

  return window.location.href;
}

/** The anonymous visitor ID, or "" when functional cookies were refused (the
 * capture script only writes the cookie when they are allowed). Read it for lead
 * payloads so a CRM record can be joined back to that person's own visits. */
export function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  for (const entry of document.cookie.split(";")) {
    const [name, ...value] = entry.trim().split("=");
    if (name !== VISITOR_ID_COOKIE) continue;
    try {
      return decodeURIComponent(value.join("="));
    } catch {
      return "";
    }
  }
  return "";
}

/** Called when functional cookies are withdrawn: drop the ID and blank the GTM
 * variable, so the next visit starts as a genuinely new visitor. */
export function clearVisitorId(): void {
  if (typeof window === "undefined") return;

  const domain = /(^|\.)office-hub\.com$/.test(window.location.hostname)
    ? ";domain=.office-hub.com"
    : "";
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${VISITOR_ID_COOKIE}=;max-age=0;path=/${domain}${secure};SameSite=Lax`;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ visitor_id: "" });
}

/** The `oh_*` cookies, used when localStorage was cleared but the cookie survived. */
function readCookieIds(): Partial<Record<keyof AdClickIds, string>> {
  const found: Partial<Record<keyof AdClickIds, string>> = {};
  for (const entry of document.cookie.split(";")) {
    const [name, ...value] = entry.trim().split("=");
    if (!name?.startsWith(COOKIE_PREFIX)) continue;
    const key = name.slice(COOKIE_PREFIX.length) as keyof AdClickIds;
    if (AD_CLICK_ID_PARAMS.includes(key)) {
      try {
        found[key] = decodeURIComponent(value.join("="));
      } catch {
        // Ignore a malformed cookie instead of dropping every attribution ID.
      }
    }
  }
  return found;
}

/** The click IDs Salesforce's Apex REST contact class actually declares.
 *
 * Apex rejects the WHOLE payload when it meets a field it does not know:
 *   {"message":"Unknown field: RestWebservice_Contact.msclkid",
 *    "errorCode":"JSON_PARSER_ERROR"}
 * so spreading all four here silently broke every Add Listing submission.
 * `gclid` has always been declared; `msclkid`, `gbraid` and `wbraid` have not.
 *
 * Widen this the moment the Apex class declares them — nothing else needs to
 * change, because all six Add Listing call sites go through here.
 * Pardot is unaffected: its form handlers take form-encoded data and ignore
 * unknown fields, so `submitPardotFormHandler` still sends all four. */
export function getSalesforceAdClickIds(): { gclid: string } {
  return { gclid: getAdClickIds().gclid };
}

/** An explicit "no" to advertising cookies. No stored decision at all means the
 * region default applies, which the capture script already enforces — so only a
 * recorded decision counts here, and a malformed one is treated as a refusal. */
function advertisingDenied(): boolean {
  try {
    const stored = localStorage.getItem("cookieConsent");
    if (!stored) return false;
    return (JSON.parse(stored).preferences || {}).advertising !== true;
  } catch {
    return false;
  }
}

/** Click IDs for a lead payload: current URL first, then pending, then stored
 * (< 90 days), then the cookie.
 *
 * Returns nothing at all once advertising cookies have been refused. The URL is
 * checked first, so without this a visitor who revoked consent on the landing
 * page would still have the click ID sent to Pardot with any form they then
 * submitted on that same page — storage cleared, but the ID still transmitted. */
export function getAdClickIds(): AdClickIds {
  const ids: AdClickIds = { msclkid: "", gclid: "", gbraid: "", wbraid: "" };
  if (typeof window === "undefined") return ids;
  if (advertisingDenied()) {
    console.log("[ad-click] advertising cookies refused - no IDs attached");
    return ids;
  }

  const url = new URLSearchParams(window.location.search);
  const pending = readPending()?.ids ?? {};
  const stored = readStoredIds();
  const cookies = readCookieIds();
  const now = Date.now();
  for (const key of AD_CLICK_ID_PARAMS) {
    const saved = pending[key] ?? stored[key];
    ids[key] =
      url.get(key) ||
      url.get(`utm_${key}`) ||
      (saved && now - saved.t < MAX_AGE_MS ? saved.v : "") ||
      cookies[key] ||
      "";
  }
  console.log("[ad-click] IDs attached to this submission", ids);
  return ids;
}

/** Mirror stored IDs into the first-party cookies and this page's dataLayer.
 * The inline capture script does the same thing on load; this is the path for
 * IDs that were held in page memory until advertising consent was granted. */
function publishAdClickIds(ids: StoredIds): void {
  const now = Date.now();
  const domain = /(^|\.)office-hub\.com$/.test(window.location.hostname)
    ? ";domain=.office-hub.com"
    : "";
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  const dataLayerEntry: Record<string, string | boolean> = {};
  const written: string[] = [];

  for (const key of AD_CLICK_ID_PARAMS) {
    const saved = ids[key];
    const age = saved ? now - saved.t : 0;
    const value = saved && age < MAX_AGE_MS ? saved.v : "";
    dataLayerEntry[key] = value;
    dataLayerEntry[`${key}_present`] = Boolean(value);
    if (value) {
      // Expires 90 days after the click itself, not 90 days from now.
      const maxAge = Math.floor((MAX_AGE_MS - age) / 1000);
      document.cookie = `${COOKIE_PREFIX}${key}=${encodeURIComponent(value)};max-age=${maxAge};path=/${domain}${secure};SameSite=Lax`;
      written.push(`${COOKIE_PREFIX}${key}`);
    }
  }
  dataLayerEntry.landing_page_url = getLandingPageUrl();
  dataLayerEntry.page_url = window.location.href;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataLayerEntry);

  (window as unknown as Record<string, unknown>)[DEBUG_GLOBAL] = {
    dataLayer: dataLayerEntry,
    cookies: written,
    at: new Date().toISOString(),
  };
  console.log("[ad-click] published after consent", {
    cookies: written,
    dataLayer: dataLayerEntry,
  });
}

/** Call when advertising consent is granted: stores IDs captured before consent. */
export function persistPendingAdClickIds(): void {
  if (typeof window === "undefined") return;
  const pending = readPending();
  if (!pending) return;

  try {
    const merged: StoredIds = { ...readStoredIds(), ...pending.ids };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    localStorage.setItem(LANDING_URL_KEY, JSON.stringify(pending.landingUrl));
    delete (window as unknown as Record<string, unknown>)[PENDING_GLOBAL];
    publishAdClickIds(merged);
  } catch {
    // ignore quota / private mode
  }
}

/** Remove advertising attribution when consent is withdrawn and clear GTM's
 * data model so a later lead event cannot reuse a previously allowed click. */
export function clearAdClickIds(): void {
  if (typeof window === "undefined") return;

  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LANDING_URL_KEY);
  delete (window as unknown as Record<string, unknown>)[PENDING_GLOBAL];
  delete (window as unknown as Record<string, unknown>)[DEBUG_GLOBAL];

  const domain = /(^|\.)office-hub\.com$/.test(window.location.hostname)
    ? ";domain=.office-hub.com"
    : "";
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  const cleared: Record<string, string | boolean> = {
    landing_page_url: window.location.href,
    page_url: window.location.href,
  };

  for (const key of AD_CLICK_ID_PARAMS) {
    document.cookie = `${COOKIE_PREFIX}${key}=;max-age=0;path=/${domain}${secure};SameSite=Lax`;
    cleared[key] = "";
    cleared[`${key}_present`] = false;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleared);
}
